// Content Hub Page
import React, { useState, useEffect, useMemo } from 'react';
import { Link, useHistory } from 'react-router-dom';
import '../../App.css';
import './Content.css';
import Footer from '../Footer';

const CATEGORY_CONFIG = {
  azure: { color: '#0078D4', label: 'Azure' },
  ai:    { color: '#FF6B6B', label: 'AI' },
  m365:  { color: '#7FBA00', label: 'M365' },
};

export default function Content() {
  const history = useHistory();
  const [allBlogs, setAllBlogs] = useState([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetch('/api/blogs')
      .then(r => r.ok ? r.json() : [])
      .then(data => setAllBlogs(data))
      .catch(() => {});
  }, []);

  // All unique tags for suggestions
  const allTags = useMemo(() => {
    const tagSet = new Set();
    allBlogs.forEach(b => (b.tags || []).forEach(t => tagSet.add(t)));
    return [...tagSet].sort();
  }, [allBlogs]);

  // Filtered results when searching
  const searchResults = useMemo(() => {
    if (!search.trim()) return [];
    const q = search.toLowerCase();
    return allBlogs.filter(b =>
      b.title.toLowerCase().includes(q) ||
      b.category.toLowerCase().includes(q) ||
      (b.tags || []).some(t => t.toLowerCase().includes(q))
    ).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [allBlogs, search]);

  const handleTagClick = (tag) => setSearch(tag);

  return (
    <>
      <div className="content-hub">
        <div className="content-hero">
          <h1>Blog Content</h1>
          <p>Explore articles and tutorials on Azure, AI, and M365</p>

          {/* Search bar */}
          <div className="content-search-wrapper">
            <div className="content-search-input-row">
              <i className="fas fa-search content-search-icon"></i>
              <input
                type="text"
                className="content-search-input"
                placeholder="Search by tag, category, or title..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              {search && (
                <button className="content-search-clear" onClick={() => setSearch('')}>
                  <i className="fas fa-times"></i>
                </button>
              )}
            </div>

            {/* Tag suggestions when idle */}
            {!search && allTags.length > 0 && (
              <div className="content-tag-suggestions">
                {allTags.slice(0, 14).map(tag => (
                  <button key={tag} className="content-tag-suggestion" onClick={() => handleTagClick(tag)}>
                    #{tag}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Search results */}
        {search.trim() && (
          <div className="content-search-results">
            <p className="content-results-count">
              {searchResults.length === 0
                ? 'No posts found'
                : `${searchResults.length} post${searchResults.length !== 1 ? 's' : ''} found`}
              {' '}
              <button className="content-clear-filters" onClick={() => setSearch('')}>Clear</button>
            </p>

            {searchResults.length > 0 && (
              <div className="content-results-grid">
                {searchResults.map(blog => {
                  const cfg = CATEGORY_CONFIG[blog.category] || { color: '#4BC4B9', label: blog.category };
                  return (
                    <div
                      key={blog.id}
                      className="content-result-card"
                      onClick={() => history.push(`/blog/${blog.id}`)}
                    >
                      <div className="content-result-header">
                        <span className="content-result-cat" style={{ backgroundColor: cfg.color }}>
                          {cfg.label.toUpperCase()}
                        </span>
                        <span className="content-result-date">
                          {new Date(blog.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                      </div>
                      <h3 className="content-result-title">{blog.title}</h3>
                      <p className="content-result-excerpt">{blog.excerpt}</p>
                      <div className="content-result-tags">
                        {(blog.tags || []).slice(0, 4).map(tag => (
                          <span
                            key={tag}
                            className="content-result-tag"
                            onClick={e => { e.stopPropagation(); handleTagClick(tag); }}
                          >
                            #{tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Category tiles */}
        <div className="content-categories">
          <Link to="/content/azure" className="category-card category-azure">
            <div className="category-icon">
              <i className="fas fa-cloud"></i>
            </div>
            <h2>Azure</h2>
            <p>Cloud computing, DevOps, and Azure services</p>
          </Link>

          <Link to="/content/ai" className="category-card category-ai">
            <div className="category-icon">
              <i className="fas fa-brain"></i>
            </div>
            <h2>AI</h2>
            <p>Artificial Intelligence, Machine Learning, and more</p>
          </Link>

          <Link to="/content/m365" className="category-card category-m365">
            <div className="category-icon">
              <i className="fab fa-microsoft"></i>
            </div>
            <h2>M365</h2>
            <p>Microsoft 365, Teams, SharePoint, and productivity tools</p>
          </Link>
        </div>
      </div>
      <Footer />
    </>
  );
}
